import React, { useEffect, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2 } from 'react-feather';
import { FundContext } from '../FundPage';
import { PageContentBodyGrid } from '../../../Shared/Page/Page';
import useWindowDimensions, { useStorage, format2dp } from '../../../../Utils/Utils';
import PageGridItem from '../../../Shared/Page/PageGridItem';
import DetailField from '../../../Shared/DetailSection/DetailField';
import DetailSectionHeader from '../../../Shared/DetailSection/DetailSectionHeader';
import Table from '../../../../components/Table/Table';
import "../FundPage.scss"
import SearchField from '../../../../components/Input/SearchField';


const FundPerformance = ({ portfolio }) => {
	const { setSelectedTab } = useContext(FundContext)
	const { fundId } = useParams()
	const { width } = useWindowDimensions()
	const storage = useStorage()

	const [performance, setPerformance] = useState(null)
	const [annualPerformance, setAnnualPerformance] = useState(null)
	const [portfolioPerformance, setPortfolioPerformance] = useState(null)

	const [dates, setDates] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);

	useEffect(() => {
		if (!selectedDate || !performance) {
			return;
		}
		setPortfolioPerformance(performance[selectedDate])

	}, [selectedDate])

	useEffect(() => {
		setSelectedTab("Performance")
		storage.getOrFetch(`funds/portfolio/dynamic/${fundId}/performance`).then(fetchPerformance => {
			if (portfolio) {
				setPerformance(fetchPerformance);
				const keys = Object.keys(fetchPerformance).sort((a, b) => new Date(b) - new Date(a));
				setDates(keys); // Set the dates from the keys of the performance object

				if (keys.length > 0) {
					setSelectedDate(keys[0]) // Set the first date as default
				}

			} else {
				if (fetchPerformance.length > 0) {
					const promisedPerformmance = Promise.all(
						fetchPerformance.map(p => {
							if (p.sinceInception) {
								const inception = [
									{ name: "Return", type: "(Net of annual fund charges and tax since fund started or most recent 10-year period (%)", value: p.fundReturn ? p.fundReturn.toFixed(2) : null },
									{ name: "Market index return since fund started or most recent 10-year period (%)", type: "Net of annual fund charges and gross of tax", value: p.benchmarkReturn ? p.benchmarkReturn.toFixed(2) : null }
								]
								return inception
							} else if (p.periodInMonths === 12) {
								const oneYear = [
									{ name: "Past year return (%)", type: "Net of annual fund charges and tax", value: p.fundReturn ? p.fundReturn.toFixed(2) : null },
									{ name: "Past year return (%)", type: "Net of annual fund charges and gross of tax", value: p.fundReturnBeforeTax ? p.fundReturnBeforeTax.toFixed(2) : null },
									{ name: "Market index past year return (%)", type: "No deductions for charges and tax", value: p.benchmarkReturn ? p.benchmarkReturn.toFixed(2) : null }
								]
								return oneYear
							} else if (p.periodInMonths === 36) {
								const threeYers = [
									{ name: "Average 3 year return (%)", type: "Net of annual fund charges and tax", value: p.fundReturn ? p.fundReturn.toFixed(2) : null },
									{ name: "Average 3 year return (%)", type: "Net of annual fund charges and gross of tax", value: p.fundReturnBeforeTax ? p.fundReturnBeforeTax.toFixed(2) : null },
									{ name: "Market index average 3 year return (%)", type: "No deductions for charges and tax", value: p.benchmarkReturn ? p.benchmarkReturn.toFixed(2) : null }
								]
								return threeYers
							} else return null
						})
					)

					promisedPerformmance.then(results => {
						const filteredResults = results.filter(result => result !== null)
						// WHERE DO WE GET THE "lastUpdated" DATE FROM?
						setAnnualPerformance({ lastUpdated: "-", performance: filteredResults[2] })
						setPerformance({ lastUpdated: "-", performance: [...filteredResults[0], ...filteredResults[1]] })
					})

				} else {
					setAnnualPerformance({ lastUpdated: "-", performance: [] })
					setPerformance({ lastUpdated: "-", performance: [] })
				}
			}

		}).catch(() => {
			setAnnualPerformance({ lastUpdated: "-", performance: [] })
			setPerformance({ lastUpdated: "-", performance: [] })
		})
	}, [])
	// funds
	const colWidths = ['auto', '20%']

	const dataMap = (data) => data && data.map((performanceReturn, idx) => {
		const { name, type, value } = performanceReturn
		return (
			<>
				<tr key={idx} onClick={() => null/*changeRoute(ROUTE_PATHS[PAGES.ADMIN] + '/fund-management/funds/' + fund.id)*/}>
					<td>
						<DetailField
							type="date"
							variant="bordered-light"
							editMode={false}
							onChange={(e) => null}
							value={name}
						/>
						<DetailField
							type="date"
							variant="bordered-light"
							editMode={false}
							onChange={(e) => null}
							value={type}
							style={{ fontSize: "0.85rem", color: "#ADB1BD", paddingTop: "0.5rem" }}
						/>
					</td>
					<td>
						<DetailField
							type="date"
							variant="bordered-light"
							editMode={false}
							onChange={(e) => null}
							value={value}
							style={{ textAlign: "right", paddingRight: "0.5rem" }}
						/>
					</td>
				</tr>
			</>
		);
	})

	// portfolios
	const portfolioColWidths = ["40%", "20%", "20%", "20%"]
	const headers = [
		"",
		<p className='performance-returns-header text-align-right'>Total Fund Return (net)</p>,
		<p className='performance-returns-header text-align-right'>Benchmark Return</p>,
		<p className='performance-returns-header text-align-right'>Total Excess Return (net)</p>
	];

	const portfolioDataMap = (data) => data && data.map((returnPeriod, idx) => {
		return (
			<tr key={idx}>
				<td>{returnPeriod.periodInWords}</td>
				<td className='text-align-right pr-s'>{format2dp(returnPeriod.fundReturn)}</td>
				<td className='text-align-right pr-s'>{format2dp(returnPeriod.benchmarkReturn)}</td>
				<td className='text-align-right pr-s'>{format2dp(returnPeriod.fundReturn - returnPeriod.benchmarkReturn)}</td>
			</tr>
		);
	})

	console.log("Portfolio Performance", portfolioPerformance)
	return (
		portfolio ?
			<PageGridItem container col="1 / span 12" direction='col' gap="xl" style={{ display: 'flex' }}>
				<PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid>
					<PageGridItem container col="1 / span 12" direction='row' justifyContent='between'>
						<DetailSectionHeader header="Performance" icon={<BarChart2 />} />
						<SearchField
                        placeholder="Date"
                        onChange={(e) => setSelectedDate(e.value)}
                        value={selectedDate && { label: selectedDate, value: selectedDate }}
                        options={dates.map((s, index) => ({ label: s, value: s }))}
                        width="m"
                        size="small"
                    />
						<p className='helper-text font-size-body-s'>{"Last Updated -"}</p>
					</PageGridItem>
					<PageGridItem container col="1 / span 12">
						<Table 
						minWidth={450} 
						colWidths={portfolioColWidths} 
						tableStyle={{ maxWidth: 1200 }} 
						headers={headers} 
						dataMap={portfolioDataMap(portfolioPerformance)} />
					</PageGridItem>
				</PageContentBodyGrid>
			</PageGridItem>
			: <>
				<PageGridItem container col="1 / span 6" direction='col' gap="xl" style={{ display: 'flex' }}>
					<PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid>
						<PageGridItem container col="1 / span 6" direction='row' justifyContent='between'>
							<DetailSectionHeader header="Performance" icon={<BarChart2 />} />
							{/* <p className='helper-text font-size-body-s'>{"Last Updated " + performance?.lastUpdated}</p> */}
						</PageGridItem>
						<PageGridItem container col="1 / span 6">
							<Table
								minWidth={300}
								colWidths={colWidths}
								dataMap={dataMap(performance?.performance)}
								id='fund-personnel-table'
							/>
						</PageGridItem>
					</PageContentBodyGrid>
				</PageGridItem>
				<PageGridItem container col="7 / span 6">
					<PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid divider={width < 1200 ? null : 'left'} paddingTop={width < 1200 ? "1.5rem" : null}>
						<PageGridItem container col="1 / span 6" direction='row' justifyContent='between'>
							<DetailSectionHeader header="Annual Past Performance" icon={<BarChart2 />} />
							<p className='helper-text font-size-body-s'>{"Last Updated " + annualPerformance?.lastUpdated}</p>
						</PageGridItem>
						<PageGridItem col={width < 1200 ? "1 / span 12" : "1 / span 6"}>
							<Table
								minWidth={300}
								colWidths={colWidths}
								dataMap={dataMap(annualPerformance?.performance)}
								id='fund-personnel-table'
							/>
						</PageGridItem>
					</PageContentBodyGrid>
				</PageGridItem>
			</>
	);
}

export default FundPerformance;